import { mock } from 'common/services/@base/mock-url.util';
import ApiClient from 'common/services/http-client';
import { Service } from 'common/services/service';
import {
  DefaultPaymentMethod,
  Deposit,
  DepositStatus,
  Discount,
  EStatusCardInfo,
  Merchant,
  MerchantDepositDetail,
  Wallet,
} from 'deposit/services/types';

import END_POINTS from 'common/config/constants/urls';
import { AstroCustomHeaders } from 'common/helpers/cookiesHandler';
import { filterValidPaymentMethods } from 'common/helpers/payment-method';
import { buildPathWithParams } from 'common/helpers/url';
import { toCamelCaseObject, toSnakeCaseObject } from 'common/helpers/utils';
import { BasicUser } from 'common/store/session/types';

export interface DepositInfoResponse {
  deposit: Deposit;
  merchant: Merchant;
  wallet: Wallet;
}

export interface DepositInfo {
  deposit: Deposit;
  merchant: Merchant;
  wallet: Wallet;
  discounts: Discount[];
  depositFinalAmount: number;
}

export interface WalletDepositRequest {
  deposit_token: string;
  currency: string;
}

export enum PurchaseOperation {
  APC = 'APC',
  APC_PURCHASE = 'APC_PURCHASE',
  PREPAID_CARD = 'PREPAID_CARD',
  WALLET_BALANCE = 'WALLET_BALANCE',
  PAYMENT_LINK = 'PAYMENT_LINK',
}

export enum DepositSourcePM {
  WALLET = 'wallet',
  APC = 'APC',
  MANUAL = 'redirect',
  CC = 'EC',
  DEBIT = 'DR',
  CREDIT = 'CW',
  VOUCHER = 'voucher',
}

export interface DepositRequest {
  paymentMethod: string;
  amount: number;
  currency: string;
  paymentElementExternalId?: string;
  depositExternalId?: string;
  merchantDepositToken?: string;
  operation: PurchaseOperation;
  finalOperationType?: PurchaseOperation;
  paymentLinkExternalId?: string;
}
export interface Purchase {
  walletMovementId: number;
  purchaseId: number;
  payment: PaymentDataPurchase;
  template: TemplatePurchase;
  referenceId?: string;
  isFtd?: boolean;
  flowTemplate?: FlowTemplates;
}

export interface PaymentInfo {
  status: PurchaseStatus;
  payinExternalId?: string;
  purchaseFlowTemplate?: FlowTemplates;
  pendingAction: {
    type: string;
    fallbackUrl: string;
  };
  statusReason: string;
}

export interface PurchaseWaiting {
  status: PurchaseStatus;
  paymentInfo?: PaymentInfo;
}

export interface PaymentLinkPurchaseWaiting {
  status: PurchaseStatus;
  referenceId?: string;
}

export interface PaymentMethodReportData {
  templateId: number;
  mandatory: boolean;
  message: string;
  fields: DynamicFormField[];
}

export interface PaymentDataPurchase {
  amount: number;
  currency: string;
  status: PurchaseStatus;
  paymentAmount: number;
  paymentCurrency: string;
  payinExternalId: string;
  description: string;
  showAlert: boolean;
  reportData: boolean;
  url: string; //@todo only before native view
}

export interface TemplatePurchase extends PaymentMethodTemplate {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  reportData?: PaymentMethodReportData;
}
export interface PushRequest {
  deposit_token: string;
}
export interface CardDepositRequest {
  card_number: string;
  cvv: string;
  expiration_date: string;
  deposit_external_id: string;
  code: string;
}

export interface ManualDepositMapi {
  number?: string;
  cvv?: string;
  expMonth?: string;
  expYear?: string;
  depositId?: string;
  code?: string;
}

export interface CardInfo {
  idCard: number;
  cardCvv: string;
  cardNumber: string;
  expirationMonth: string;
  expirationYear: string;
  currency: string;
  availableCredit: number;
  balanceEnabled?: boolean;
  code?: string;
  status?: EStatusCardInfo | string;
}

export interface CardsResponse {
  cards: CardInfo[];
  page: {
    page_number: number;
    page_size: number;
    total: number;
  };
}

export interface PreviewAuthResponse {
  description: string;
  error: string;
  internalTerm: string;
  message: string;
  status: number;
  requiredFields?: string[];
}
export interface PreviewSummResponse {
  duration: string;
  requestAmount: number;
  requestCurrency: string;
  userCurrency: string;
  userTax: number;
  userTotal: number;
  flowTemplate?: FlowTemplates;
}

export enum TemplateType {
  REDIRECT = 'REDIRECT',
  BROWSER = 'BROWSER',
  ME = 'ME',
  BL = 'BL',
  BD_BR = 'BD_BR',
  BD_UY = 'BD_UY',
  BD_MX = 'BD_MX',
  BD_AR = 'BD_AR',
  BD_CL = 'BD_CL',
  OXXO_MX = 'OXXO_MX',
  SPEI_MX = 'SPEI_MX',
  PAYNET_MX = 'PAYNET_MX',
  CRYPTO = 'CRYPTO',
}

export enum PurchaseStatus {
  CREATE = 'CREATED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  CANCELLED = 'CANCELLED',
  FOR_REVIEW = 'FOR_REVIEW',
  ACTIVE = 'ACTIVE',
}

export interface PaymentMethodTemplate {
  type: TemplateType;
  description: string;
  showAlert: boolean;
}

export interface DynamicFormField {
  fieldId: number;
  fieldKey: string;
  priority: number;
  title: string;
  type: string;
  mandatory: boolean;
  hint: string;
  regex: string;
  mask: string | null;
  keyboardType: string;
  options: { option_id: number; value: string; name: string; priority: number }[];
}

export enum PaymentMethodStatus {
  ACTIVE = 'ACTIVE',
  INFORMATION = 'INFORMATION',
  WARNING = 'WARNING',
  OUT_OF_SERVICE = 'OUT_OF_SERVICE',
  ERROR = 'ERROR',
  LOYALTY = 'LOYALTY',
}

export enum FlowTemplates {
  CC = 'CC_VGS',
  NATIVE = 'PIX_NATIVE',
  DEFAULT = 'DEFAULT',
}
export interface PaymentMethod {
  id: number;
  type: string;
  code: string;
  name: string;
  image: string;
  priority: number;
  pmDescription: string;
  description: string;
  searchString?: string;
  appScheme?: string;
  flowTemplate: FlowTemplates;
  duration: string;
  status: {
    active: boolean;
    data: PaymentMethodData;
  };
  subscriptionEnabled: boolean;
  additionalImages?: [string];
  payByAstropay?: boolean;
}
export interface PaymentMethodData {
  type: PaymentMethodStatus;
  description?: string;
  id?: number;
  name?: string;
  url?: string; // local, only for reseller
}

export interface PaymentMethodsResponse {
  payment_methods: PaymentMethod[];
}

export interface DefaultPM {
  code: string;
  name: string;
}

export interface CreditCard {
  expirationDate: string;
  firstSixDigits: string;
  holderName: string;
  img: string;
  lastFourDigits: string;
  paymentElementExternalId: string;
  selected: boolean;
  type: string;
}

export interface OneTouchInit {
  depositData: MerchantDepositDetail;
  wallet?: Wallet;
  user: BasicUser;
  defaultPaymentMethod?: DefaultPaymentMethod;
  paymentElements?: CreditCard[];
}

export interface AstrocoinsReward {
  message: string;
  rewardAvailable: boolean;
  shortMessage?: string;
  description?: string;
}

export interface DepositStatusResponse {
  status: DepositStatus;
}

export interface PromoCodeRequest {
  depositExternalId: string;
  promoCode: string;
}

export interface WalletDepositResponse {
  status?: DepositStatus;
  redirectUrl?: string;
}

class DepositService extends Service {
  static initOneTouch = async (token: string) => {
    const cancelToken = DepositService.setCall('initOneTouch', ApiClient.cancelTokenSource());
    try {
      const response = await ApiClient.get<OneTouchInit>(
        buildPathWithParams(END_POINTS.initOneTouch, { token }),
        {},
        { cancelToken },
      );
      return toCamelCaseObject<OneTouchInit>(response);
    } catch {
      return undefined;
    }
  };

  static getPreviewAuth = async (request: DepositRequest) => {
    try {
      const response = ApiClient.post<PreviewAuthResponse>(
        END_POINTS.getPreviewAuth,
        toSnakeCaseObject(request),
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static getPreviewSummary = async (request: DepositRequest) => {
    try {
      const response = await ApiClient.post<PreviewSummResponse>(
        END_POINTS.getPreviewSummary,
        toSnakeCaseObject(request),
      );
      return toCamelCaseObject<PreviewSummResponse>(response);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static getDeposit = async (token: string): Promise<DepositInfoResponse> => {
    return ApiClient.get(buildPathWithParams(END_POINTS.getDepositDetail, { token }));
  };

  static attemptDeposit = async (request: DepositRequest) => {
    try {
      const response = await ApiClient.post<Purchase>(
        END_POINTS.deposit,
        toSnakeCaseObject(request),
      );
      return toCamelCaseObject<Purchase>(response);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static attemptWalletDeposit = async (currency: string, deposit_token: string) => {
    try {
      const resp = await ApiClient.post<WalletDepositResponse>(END_POINTS.walletDeposit, {
        currency,
        deposit_token,
      });
      return toCamelCaseObject<WalletDepositResponse>(resp);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static attemptCardDeposit = async (request: ManualDepositMapi) => {
    try {
      const resp = await ApiClient.post(
        END_POINTS.manualDepositViaMapi,
        toSnakeCaseObject(request),
      );
      return toCamelCaseObject<WalletDepositResponse>(resp);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static getDefaultPM = async () => {
    try {
      const response = await ApiClient.get<DefaultPM>(END_POINTS.defaultPM);
      return toCamelCaseObject<DefaultPM>(response);
    } catch {
      return Promise.resolve({
        code: '',
        name: '',
      });
    }
  };

  static setDefaultPM = async (code: string) => {
    return ApiClient.put<DefaultPM>(END_POINTS.defaultPM, { code });
  };

  static checkPurchaseStatus = async (
    purchaseId: number,
    astroHeaders?: Record<AstroCustomHeaders, string>,
  ) => {
    try {
      const response = await ApiClient.get<PurchaseWaiting>(
        mock(
          buildPathWithParams(END_POINTS.getPaymentStatus, { purchaseId: purchaseId.toString() }),
          false,
        ),
        {},
        { headers: astroHeaders },
      );
      return toCamelCaseObject<PurchaseWaiting>(response);
    } catch {
      return { status: PurchaseStatus.PENDING };
    }
  };

  static sendPromoCode = async (request: PromoCodeRequest) => {
    try {
      const response = await ApiClient.post(END_POINTS.submitPromoCode, toSnakeCaseObject(request));
      return toCamelCaseObject<{
        isValid: boolean;
        detail?: string;
      }>(response);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  static getPaymentMethods = async (
    currency: string,
    amount: number,
    depositExternalId?: string,
  ) => {
    try {
      const cancelToken = DepositService.setCall('initOneTouch', ApiClient.cancelTokenSource());
      const params = depositExternalId
        ? { product_type: 'WALLET_BALANCE', currency, amount, depositExternalId }
        : { product_type: 'WALLET_BALANCE', currency, amount };

      const response = await ApiClient.get<PaymentMethodsResponse>(
        END_POINTS.getPaymentMethods,
        toSnakeCaseObject(params),
        { cancelToken },
      );

      return (
        filterValidPaymentMethods(response.payment_methods).map((pm) =>
          toCamelCaseObject<PaymentMethod>(pm),
        ) ?? []
      );
    } catch {
      return [] as PaymentMethod[];
    }
  };

  static getCards = async (
    amount?: number,
    currency?: string,
    mid?: number,
    page = 1,
    size = 20,
  ) => {
    try {
      const response = await ApiClient.get<CardsResponse>(END_POINTS.getCardsList, {
        amount: amount,
        currency: currency,
        merchantId: mid,
        page: page,
        size: size,
      });
      const cards = response.cards.map((c) => toCamelCaseObject<CardInfo>(c)) ?? [];
      const respPages = response.page;

      return { cards, page: respPages };
    } catch {
      return {} as CardsResponse;
    }
  };

  static pushNotification = async (request: PushRequest) => {
    return ApiClient.post(END_POINTS.pushNotification, request);
  };

  static cancelInitOneTouch = () => {
    DepositService.cancelRequest('initOneTouch');
  };

  static cancelGetPaymentMethods = () => {
    DepositService.cancelRequest('getPaymentMethods');
  };
}

export default DepositService;
